'use client';

import { cn } from '@/lib/utils';
import { ReactNode } from 'react';

interface BackgroundLayersProps {
  children?: ReactNode;
  className?: string;
  variant?: 'default' | 'subtle' | 'intense' | 'minimal';
  showGeometric?: boolean;
}

export function BackgroundLayers({ 
  children, 
  className,
  variant = 'default',
  showGeometric = true 
}: BackgroundLayersProps) {
  return (
    <div className={cn("relative min-h-screen", className)}>
      {/* Base layered background */}
      <div 
        className={cn(
          "absolute inset-0 -z-10",
          variant === 'subtle' && "bg-layered-subtle",
          variant === 'intense' && "bg-gradient-yellow-blue",
          variant === 'minimal' && "bg-background",
          variant === 'default' && "bg-layered-subtle"
        )}
      />
      
      {/* Geometric decorative elements - no black, only yellow/blue/grey */}
      {showGeometric && (
        <>
          {/* Large blurred circles - Canva style */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow-200/15 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-blue-200/15 rounded-full blur-3xl -z-10" />
          
          {/* Modern lines/frames - subtle geometric patterns */}
          <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-brand-yellow-200/20 to-transparent -z-10" />
          <div className="absolute bottom-20 right-0 w-px h-64 bg-gradient-to-b from-transparent via-brand-blue-200/20 to-transparent -z-10" />
          
          {/* Small decorative circles */}
          <div className="absolute top-32 right-32 w-2 h-2 bg-brand-yellow-400/30 rounded-full -z-10" />
          <div className="absolute bottom-40 left-40 w-3 h-3 bg-brand-blue-400/30 rounded-full -z-10" />
        </>
      )}
      
      {/* Content */}
      {children && <div className="relative z-0">{children}</div>}
    </div>
  );
}

interface LayeredCardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'elevated' | 'flat';
}

export function LayeredCard({ children, className, variant = 'default' }: LayeredCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card text-card-foreground",
        variant === 'elevated' && "shadow-layered border-brand-blue-200/20 dark:border-brand-blue-800/20",
        variant === 'flat' && "shadow-sm border-border",
        variant === 'default' && "shadow-modern border-border",
        className
      )}
    >
      {children}
    </div>
  );
}

interface GeometricFrameProps {
  children: ReactNode;
  className?: string;
  accent?: 'yellow' | 'blue' | 'both';
}

export function GeometricFrame({ children, className, accent = 'both' }: GeometricFrameProps) {
  return (
    <div className={cn("relative", className)}>
      {/* Frame border with gradient */}
      <div
        className={cn(
          "absolute inset-0 rounded-lg border-2 -z-10",
          accent === 'yellow' && "border-brand-yellow-300/30 dark:border-brand-yellow-700/30",
          accent === 'blue' && "border-brand-blue-300/30 dark:border-brand-blue-700/30",
          accent === 'both' && "border-transparent bg-gradient-to-br from-brand-yellow-200/20 via-transparent to-brand-blue-200/20 dark:from-brand-yellow-800/10 dark:via-transparent dark:to-brand-blue-800/10"
        )}
        style={{
          maskImage: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          WebkitMaskComposite: 'xor',
          padding: '2px',
        }}
      />
      
      {/* Inner content */}
      <div className="relative bg-card rounded-lg p-6">
        {children}
      </div>
    </div>
  );
}

